import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';

const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg'];

const getSavedSlides = () => {
  try {
    const raw = localStorage.getItem('app.slider');
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) { return []; }
};

const getImageUrl = (filename) => {
  if (!filename) return '';
  if (filename.startsWith('http') || filename.startsWith('data:')) return filename;
  const base = process.env.REACT_APP_FILE_BASE_URL;
  return `${base}/uploads/documents/${filename}`;
};

// عرض هر نسخهٔ ریسپانسیو (باید با مقادیر سمت سرور هماهنگ باشد)
const RESPONSIVE_WIDTHS = { mobile: 640, tablet: 1024, laptop: 1440, desktop: 1920 };

const buildSrcSet = (images) => {
  if (!images) return undefined;
  const parts = Object.entries(RESPONSIVE_WIDTHS)
    .filter(([key]) => images[key])
    .map(([key, width]) => `${getImageUrl(images[key])} ${width}w`);
  return parts.length ? parts.join(', ') : undefined;
};

const ImageSlider = ({ slides: propSlides }) => {
  const [slides, setSlides] = useState(propSlides || getSavedSlides());
  const [index, setIndex] = useState(0);
  const timer = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const sliderRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const videoRefs = useRef({});

  useEffect(() => {
    if (propSlides) setSlides(propSlides);
  }, [propSlides]);

  useEffect(() => {
    if (!slides || slides.length === 0) return;
    const current = slides[index];
    const currentImage = current?.image || '';
    const isCurrentVideo = VIDEO_EXTENSIONS.some((ext) => currentImage.toLowerCase().endsWith(ext));

    if (!isCurrentVideo) {
      setIsVideoPlaying(false);
    }

    Object.entries(videoRefs.current).forEach(([key, video]) => {
      if (!video) return;
      if (Number(key) !== index) {
        video.pause();
        video.currentTime = 0;
      }
    });

    if (isCurrentVideo) {
      const currentVideo = videoRefs.current[index];
      if (currentVideo) {
        currentVideo.muted = true;
        setIsVideoPlaying(true);
        clearInterval(timer.current);
        const playPromise = currentVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // autoplay can be blocked by browser policies
          });
        }
      }
    }
  }, [index, slides]);

  useEffect(() => {
    if (!slides || slides.length <= 1 || isVideoPlaying) return;
    timer.current = setInterval(() => setIndex(i => (i + 1) % slides.length), 4000);
    return () => clearInterval(timer.current);
  }, [slides, isVideoPlaying]);

  if (!slides || slides.length === 0) return null;

  const go = (i) => { setIndex(i); clearInterval(timer.current); };
  const next = () => setIndex(i => (i + 1) % slides.length);
  const prev = () => setIndex(i => (i - 1 + slides.length) % slides.length);

  const handleTouchStart = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
  };

  const handleTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch || touchStartX.current === null || touchStartY.current === null) return;

    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    const threshold = 40;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold) {
      clearInterval(timer.current);
      if (deltaX < 0) {
        next();
      } else {
        prev();
      }
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div
      ref={sliderRef}
      className="app-image-slider"
      style={{ position: 'relative', overflow: 'hidden', borderRadius: 12, marginBottom: 12 }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div style={{ position: 'relative', width: '100%', height: 240 }}>
        {slides.map((s, i) => {
          const visible = i === index;
          const isVideo = s.image && VIDEO_EXTENSIONS.some(ext => s.image.toLowerCase().endsWith(ext));
          return (
            <div
              key={s.id || i}
              style={{
                position: 'absolute', left: 0, top: 0, width: '100%', height: '100%',
                transition: 'opacity 700ms cubic-bezier(.22,.9,.29,1), transform 700ms cubic-bezier(.22,.9,.29,1)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.995)',
                pointerEvents: visible ? 'auto' : 'none'
              }}
            >
              {isVideo ? (
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  <video
                    ref={(el) => { videoRefs.current[i] = el; }}
                    src={getImageUrl(s.image) || s.image}
                    poster={getImageUrl(s.poster)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 0 }}
                    preload="metadata"
                    title={s.title}
                    defaultMuted
                    controls={visible}
                    playsInline
                    onPlay={() => {
                      if (visible) {
                        setIsVideoPlaying(true);
                        clearInterval(timer.current);
                      }
                    }}
                    onPause={() => {
                      if (visible) {
                        setIsVideoPlaying(true);
                      }
                    }}
                    onEnded={() => {
                      if (!visible) return;
                      setIsVideoPlaying(false);
                      next();
                    }}
                  />
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '12px 16px', background: 'linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.12))', color: '#fff' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{s.title}</div>
                  </div>
                </div>
              ) : (
                <a href={s.link || '#'}>
                  <img
                    src={getImageUrl(s.image) || s.image}
                    srcSet={buildSrcSet(s.images)}
                    sizes="100vw"
                    alt={s.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '12px 16px', background: 'linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.12))', color: '#fff' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{s.title}</div>
                  </div>
                </a>
              )}
            </div>
          );
        })}


      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="next-slide"
            onClick={() => { next(); clearInterval(timer.current); }}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              width: 38,
              height: 38,
              borderRadius: '50%',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(12, 18, 36, 0.45)',
              color: '#fff',
              backdropFilter: 'blur(3px)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              cursor: 'pointer'
            }}
          >
            <ChevronLeftRounded fontSize="small" />
          </button>
          <button
            type="button"
            aria-label="prev-slide"
            onClick={() => { prev(); clearInterval(timer.current); }}
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              width: 38,
              height: 38,
              borderRadius: '50%',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(12, 18, 36, 0.45)',
              color: '#fff',
              backdropFilter: 'blur(3px)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              cursor: 'pointer'
            }}
          >
            <ChevronRightRounded fontSize="small" />
          </button>
        </>
      )}

      <div style={{ position: 'absolute', left: 12, bottom: 12, display: 'flex', gap: 8 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => go(i)} aria-label={`slide-${i}`} style={{ width: 10, height: 5, borderRadius: 5, border: 'none', background: i === index ? '#fff' : 'rgba(255,255,255,0.5)' }} />
        ))}
      </div>
    </div>
  );
};

export default ImageSlider;
